<template>
  <div v-if="shouldDisplayStores" class="flex gap-8">
    <div v-if="!isClientSide" class="flex gap-8">
      <RevSkeleton height="40px" shape="rectangle" width="135px" />

      <RevSkeleton height="40px" shape="rectangle" width="127px" />
    </div>

    <template v-if="isClientSide">
      <RevButtonBase
        target="_blank"
        :to="finalPlayStoreURL"
        :tracking="{
          name: finalPlayTrackingName,
          zone: trackingZone,
          page_type: routeName,
        }"
      >
        <RevIllustration
          :alt="i18n(translations.googlePlayImageAlt)"
          class="inline-block !h-40 w-auto"
          :height="40"
          :src="`/img/socials/${countryCode}/google-play.svg`"
          :width="135"
        />
      </RevButtonBase>

      <RevButtonBase
        target="_blank"
        :to="finalAppleStoreURL"
        :tracking="{
          name: finalAppleTrackingName,
          zone: trackingZone,
          page_type: routeName,
        }"
      >
        <RevIllustration
          :alt="i18n(translations.appStoreImageAlt)"
          class="inline-block !h-40 w-auto"
          :height="40"
          :src="`/img/socials/${countryCode}/apple-store.svg`"
          :width="127"
        />
      </RevButtonBase>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRuntimeConfig } from '#imports'
import { computed, onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'

import {
  BASE_APP_STORE_URL,
  BASE_PLAY_STORE_URL,
  STORE_COUNTRIES,
} from './AppStoreButtons.constant'
import translations from './AppStoreButtons.translations'

// Note that before registering new URLs to this configuration, you should
// also download and store associated assets in `public/img/social`.

// assets may be found here: https://www.figma.com/file/jC2mtHRgj5YEWRLSQYfKHq/04.-Assets?node-id=1%3A852

type AppStoreButtonsProps = {
  campaign?: string
  trackingZone: string
  appleStoreTrackingName?: string
  playStoreTrackingName?: string
  appleStoreUrl?: string
  playStoreUrl?: string
  showInAllCountries?: boolean
}

const props = defineProps<AppStoreButtonsProps>()
const i18n = useI18n()
const { countryCode } = useMarketplace().market
const runtimeConfig = useRuntimeConfig()
const { isAppsFlyerUserConsentGiven } = useDataConsent()
const route = useRoute()

const {
  APPSFLYER_APPLE_STORE_URL,
  APPSFLYER_PLAY_STORE_URL,
  ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES,
} = runtimeConfig.public

const isAppsFlyerWithDidomiEnabled =
  ENABLE_APPSFLYER_WITH_DIDOMI_COUNTRIES?.includes(countryCode)

const isClientSide = ref(!isAppsFlyerWithDidomiEnabled)

onMounted(() => {
  isClientSide.value = true
})

const routeName = computed(() => route.name?.toString() || '')

const finalAppleTrackingName = computed(
  () => props.appleStoreTrackingName || 'redirect_app_store',
)
const finalPlayTrackingName = computed(
  () => props.playStoreTrackingName || 'redirect_google_play',
)

const showBaseStore = computed(() => {
  return isAppsFlyerWithDidomiEnabled && !isAppsFlyerUserConsentGiven.value
})

const finalAppleStoreURL = computed(() => {
  if (showBaseStore.value) return BASE_APP_STORE_URL

  const storeBaseUrl = props.appleStoreUrl || APPSFLYER_APPLE_STORE_URL

  return generateTrackingURL(storeBaseUrl)
})

const finalPlayStoreURL = computed(() => {
  if (showBaseStore.value) return BASE_PLAY_STORE_URL

  const storeBaseUrl = props.playStoreUrl || APPSFLYER_PLAY_STORE_URL

  return generateTrackingURL(storeBaseUrl)
})

const shouldDisplayStores = computed(() => {
  if (props.showInAllCountries) return true

  return STORE_COUNTRIES.includes(countryCode)
})

function generateTrackingURL(storeBaseUrl: string) {
  return `${storeBaseUrl}?&af_ad=${countryCode.toLowerCase()}&af_adset=${
    props.campaign
  }`
}
</script>
